<script setup lang="ts">
/**
 * Presentational two-section editor for the translation view (Figma node 1791:99913): a
 * read-only "Premade" section and an editable "Custom" section, each introduced by an H3 title
 * and an English/Italian flag-badge header. Premade rows live inside a SINGLE comparison card;
 * every custom row is its OWN comparison card (editable target text box). Custom rows are a
 * `customRows` v-model.
 */
import { computed } from "vue";
import type {
  PremadeTranslationRow,
  CustomTranslationRow,
} from "@/features/experience-shared/highlights-v2/translation/types/highlight-translation";
import TranslationHighlightRow from "@/features/experience-shared/highlights-v2/translation/components/TranslationHighlightRow.vue";
import NovaIconFlag from "@/ui-kit/NovaIconFlag/NovaIconFlag.vue";

const props = withDefaults(
  defineProps<{
    premadeRows?: PremadeTranslationRow[];
    sourceCountryCode: string;
    targetCountryCode: string;
    showPremade?: boolean;
    readonly?: boolean;
  }>(),
  { premadeRows: () => [], showPremade: true, readonly: false }
);

const customRows = defineModel<CustomTranslationRow[]>("customRows", { required: true });

const showPremadeSection = computed(() => props.showPremade);

function updateTranslation(id: string, translation: string): void {
  customRows.value = customRows.value.map((row) => (row.id === id ? { ...row, translation } : row));
}
</script>

<template>
  <div class="flex w-full flex-col gap-4" data-testid="premade-custom-translation-editor">
    <section v-if="showPremadeSection" class="flex w-full flex-col gap-4" data-testid="translation-premade-section">
      <h3 class="m-0 font-bold text-[16px] leading-[20px] text-nova-default">
        {{ $t("experience.highlights.v2.premade.title") }}
      </h3>

      <div class="flex w-full flex-col gap-2 overflow-clip">
        <div
          v-if="premadeRows.length > 0"
          class="flex w-full items-start gap-3"
          data-testid="translation-premade-languages"
        >
          <div class="flex min-w-0 flex-1 items-center gap-1">
            <NovaIconFlag shape="square" :size="18" :country-code="sourceCountryCode" />
            <span class="font-semibold text-[14px] leading-[18px] text-nova-default">
              {{ $t(`common.language.${sourceCountryCode}`) }}
            </span>
          </div>
          <div class="flex min-w-0 flex-1 items-center gap-1">
            <NovaIconFlag shape="square" :size="18" :country-code="targetCountryCode" />
            <span class="font-semibold text-[14px] leading-[18px] text-nova-default">
              {{ $t(`common.language.${targetCountryCode}`) }}
            </span>
          </div>
        </div>

        <div
          class="flex w-full flex-col gap-3 rounded-lg border border-neutral-60 bg-white p-3"
          data-testid="translation-premade-list"
        >
          <TranslationHighlightRow
            v-for="(row, index) in premadeRows"
            :key="row.code"
            :row-id="row.code"
            :index="index + 1"
            :source="row.source"
            :target="row.target"
            :readonly="readonly"
            :diff="row.diff"
          />
          <p
            v-if="premadeRows.length === 0"
            class="m-0 text-[14px] leading-[19px] text-nova-muted"
            data-testid="translation-premade-empty"
          >
            {{ $t("experience.highlights.v2.empty") }}
          </p>
        </div>
      </div>
    </section>

    <!-- Custom section: title + flag header + one card per custom row (editable target). -->
    <section class="flex w-full flex-col gap-4" data-testid="translation-custom-section">
      <h3 class="m-0 font-bold text-[16px] leading-[20px] text-nova-default">
        {{ $t("experience.highlights.v2.custom.title") }}
      </h3>

      <div class="flex w-full flex-col gap-2 overflow-clip">
        <div
          v-if="customRows.length > 0"
          class="flex w-full items-start gap-3"
          data-testid="translation-custom-languages"
        >
          <div class="flex min-w-0 flex-1 items-center gap-1">
            <NovaIconFlag shape="square" :size="18" :country-code="sourceCountryCode" />
            <span class="font-semibold text-[14px] leading-[18px] text-nova-default">
              {{ $t(`common.language.${sourceCountryCode}`) }}
            </span>
          </div>
          <div class="flex min-w-0 flex-1 items-center gap-1">
            <NovaIconFlag shape="square" :size="18" :country-code="targetCountryCode" />
            <span class="font-semibold text-[14px] leading-[18px] text-nova-default">
              {{ $t(`common.language.${targetCountryCode}`) }}
            </span>
          </div>
        </div>

        <div v-if="customRows.length > 0" class="flex w-full flex-col gap-2" data-testid="translation-custom-list">
          <div
            v-for="(row, index) in customRows"
            :key="row.id"
            class="flex w-full rounded-lg border border-neutral-60 bg-white p-3"
          >
            <TranslationHighlightRow
              :row-id="row.id"
              :index="index + 1"
              :source="row.source"
              :target="row.translation"
              :editable="row.editable"
              :readonly="readonly"
              :diff="row.diff"
              :error="row.error"
              @update:translation="updateTranslation(row.id, $event)"
            />
          </div>
        </div>
        <div
          v-else
          class="flex w-full rounded-lg border border-neutral-60 bg-white p-3"
          data-testid="translation-custom-empty"
        >
          <p class="m-0 text-[14px] leading-[19px] text-nova-muted">
            {{ $t("experience.highlights.v2.empty") }}
          </p>
        </div>
      </div>
    </section>
  </div>
</template>
